// src/features/payment/TypesTab.tsx
// Payment type registry — products declare their payable entity types here.
// Covers: ListPaymentTypes, RegisterPaymentType, GetPaymentType (via list item),
// UpdatePaymentType, DeletePaymentType.

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { usePaymentPaymentType, useI18n } from '@react/hooks';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { FormDialog, FormRow, TitleInput, AutoGrowTextarea } from '@/components/form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PaymentTypeDefinition } from '@core/types/payment';
import { errMessage } from './format';

interface TypeForm {
  slug: string;
  display_name: string;
  description: string;
  product_id: string;
  source_entity_type: string;
  default_currency: string;
}

const EMPTY: TypeForm = { slug: '', display_name: '', description: '', product_id: '', source_entity_type: '', default_currency: 'USD' };

export const TypesTab = observer(() => {
  const store = usePaymentPaymentType();
  const { t } = useI18n();
  const [items, setItems] = useState<PaymentTypeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PaymentTypeDefinition | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<TypeForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<PaymentTypeDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialFormRef = useRef<TypeForm>(EMPTY);
  const nameRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await store.ListPaymentTypes();
      setItems(res.definitions ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    initialFormRef.current = EMPTY;
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (d: PaymentTypeDefinition) => {
    const next: TypeForm = {
      slug: d.slug ?? '', display_name: d.display_name ?? '', description: d.description ?? '',
      product_id: d.product_id ?? '', source_entity_type: d.source_entity_type ?? '', default_currency: d.default_currency ?? 'USD',
    };
    setEditing(d);
    setForm(next);
    initialFormRef.current = next;
    setError(null);
    setFormOpen(true);
  };

  const canSubmit = !!form.slug.trim() && !!form.display_name.trim();
  const dirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdatePaymentType(editing.entity.id, {
          definition: { ...editing, display_name: form.display_name, description: form.description, default_currency: form.default_currency } as any,
          update_mask: [],
        } as any);
      } else {
        await store.RegisterPaymentType({
          entity: {} as any,
          definition: {
            entity: {} as any,
            slug: form.slug, display_name: form.display_name, description: form.description,
            product_id: form.product_id, source_entity_type: form.source_entity_type, default_currency: form.default_currency,
            retry_on: [], never_retry_on: [], allowed_methods: [], metadata: {}, field_mapping: {},
          } as any,
        } as any);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(errMessage(err, t('payment.types.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeletePaymentType(deleteTarget.entity.id);
    setDeleteTarget(null);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('payment.types.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('payment.types.create')}</Button>
      </div>

      {(store.error != null) && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('payment.types.slug')}</TableHead>
            <TableHead>{t('payment.types.displayName')}</TableHead>
            <TableHead>{t('payment.types.sourceEntityType')}</TableHead>
            <TableHead>{t('payment.types.defaultCurrency')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableEmpty colSpan={6} message={t('payment.types.empty')} />
          ) : (
            items.map((d) => (
              <TableRow key={d.entity?.id}>
                <TableCell className="font-mono text-xs">{d.slug}</TableCell>
                <TableCell>{d.display_name}</TableCell>
                <TableCell>{d.source_entity_type}</TableCell>
                <TableCell>{d.default_currency}</TableCell>
                <TableCell>{d.is_active ? <Badge variant="success" size="sm">{t('common.active')}</Badge> : <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(d)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(d)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
        submitLabel={editing ? t('common.saveChanges') : t('payment.types.create')}
        canSubmit={canSubmit}
        isSubmitting={store.isLoading}
        dirty={dirty}
        aria-label={editing ? t('payment.types.edit') : t('payment.types.create')}
      >
        <div className="space-y-2 px-3.5 pb-4 pt-2">
          {error && <p className="text-sm text-destructive">{error}</p>}

          <TitleInput
            value={form.display_name}
            onChange={(v) => setForm((p) => ({ ...p, display_name: v }))}
            placeholder={t('payment.types.displayNamePlaceholder')}
            initialFocus
            onEnter={() => nameRef.current?.focus()}
            aria-label={t('payment.types.displayName')}
          />
          <AutoGrowTextarea
            value={form.description}
            onChange={(v) => setForm((p) => ({ ...p, description: v }))}
            placeholder={t('payment.providers.descriptionPlaceholder')}
            minRows={1}
          />

          <div className="space-y-0.5 border-t border-border pt-2">
            <FormRow label={t('payment.types.slug')} labelWidth="sm">
              <input
                ref={nameRef}
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                disabled={!!editing}
                placeholder="booking.session_payment"
                autoCapitalize="off"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              />
            </FormRow>
            <FormRow label={t('payment.types.productId')} labelWidth="sm">
              <input
                value={form.product_id}
                onChange={(e) => setForm((p) => ({ ...p, product_id: e.target.value }))}
                disabled={!!editing}
                autoCapitalize="off"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              />
            </FormRow>
            <FormRow label={t('payment.types.sourceEntityType')} labelWidth="sm">
              <input
                value={form.source_entity_type}
                onChange={(e) => setForm((p) => ({ ...p, source_entity_type: e.target.value }))}
                disabled={!!editing}
                autoCapitalize="off"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              />
            </FormRow>
            <FormRow label={t('payment.types.defaultCurrency')} labelWidth="sm">
              <input
                value={form.default_currency}
                onChange={(e) => setForm((p) => ({ ...p, default_currency: e.target.value.toUpperCase() }))}
                placeholder="USD"
                autoCapitalize="off"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('payment.types.deleteConfirmTitle')}
        destructive
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default TypesTab;
